import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, ContentCopy, Delete, Download, Edit } from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const emptyForm = {
  username: '',
  password: '',
  display_name: '',
  station_id: '',
  is_enabled: true,
  notes: '',
};

const FALLBACK_INSTALL_STEPS = [
  'Choose the contributor account and enter its password, then download the package. Username, password, station, and server address are already in config/default.json.',
  'Copy the zip to the Linux sender machine and unpack it to /opt/remotecontribution.',
  'Install prerequisites: Node.js 18+, npm, and ffmpeg.',
  'If the sound card is not hw:0,0, set audio.device in /opt/remotecontribution/config/default.json.',
  'Run: cd /opt/remotecontribution && sudo chmod +x install.sh && sudo ./install.sh',
  'Verify: journalctl -u remotecontribution-headless.service -f',
  'In Clocks, choose this same contributor on a WebRTC Receiver item.',
];

/**
 * Admin UI for WebRTC contribution accounts plus Linux sender package
 * download, connection credentials, and install instructions.
 */
export default function WebrtcContributionSettings({ stations = [] }) {
  const [contributors, setContributors] = useState([]);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [connectionError, setConnectionError] = useState('');
  const [selectedContributorId, setSelectedContributorId] = useState('');
  const [setupStationId, setSetupStationId] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [embedPassword, setEmbedPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [createdCredentials, setCreatedCredentials] = useState(null);

  const selectedContributor = useMemo(
    () => contributors.find((c) => String(c.id) === String(selectedContributorId)) || null,
    [contributors, selectedContributorId]
  );

  const loadContributors = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/webrtc-contributors`, { withCredentials: true });
      setContributors(res.data || []);
    } catch (e) {
      if (e.response?.status !== 403 && e.response?.status !== 404) {
        setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to load contributors' });
      }
    }
  }, []);

  const loadConnectionInfo = useCallback(async (contributor = null) => {
    try {
      const params = {};
      if (contributor?.station_id) params.station_id = contributor.station_id;
      if (contributor?.username) params.username = contributor.username;
      const res = await axios.get(`${API_URL}/api/webrtc-contributors/connection-info`, {
        params,
        withCredentials: true,
      });
      setConnectionInfo(res.data);
      setConnectionError('');
    } catch (e) {
      setConnectionError(e.response?.data?.message || 'Could not load connection details from the server.');
    }
  }, []);

  useEffect(() => {
    loadContributors();
  }, [loadContributors]);

  useEffect(() => {
    loadConnectionInfo(selectedContributor);
  }, [loadConnectionInfo, selectedContributor]);

  useEffect(() => {
    if (!selectedContributorId && contributors.length > 0) {
      setSelectedContributorId(String(contributors[0].id));
    }
  }, [contributors, selectedContributorId]);

  useEffect(() => {
    if (selectedContributor?.station_id) {
      setSetupStationId(String(selectedContributor.station_id));
      return;
    }
    setSetupStationId((current) => {
      if (current && stations.some((station) => String(station.id) === String(current))) {
        return current;
      }
      return stations[0]?.id ? String(stations[0].id) : '';
    });
  }, [selectedContributor, stations]);

  const openDialog = (item = null) => {
    if (item) {
      setEditing(item);
      setForm({
        username: item.username || '',
        password: '',
        display_name: item.display_name || '',
        station_id: item.station_id || '',
        is_enabled: item.is_enabled !== false,
        notes: item.notes || '',
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.username.trim()) {
      setMessage({ type: 'error', text: 'Username is required' });
      return;
    }
    if (!editing && (!form.password || form.password.length < 8)) {
      setMessage({ type: 'error', text: 'Password is required (minimum 8 chars)' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {
        username: form.username.trim(),
        display_name: form.display_name || null,
        station_id: form.station_id ? parseInt(form.station_id, 10) : null,
        is_enabled: !!form.is_enabled,
        notes: form.notes || null,
      };
      if (form.password && form.password.trim()) {
        payload.password = form.password;
      }

      let saved;
      if (editing) {
        const res = await axios.put(`${API_URL}/api/webrtc-contributors/${editing.id}`, payload, { withCredentials: true });
        saved = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/webrtc-contributors`, payload, { withCredentials: true });
        saved = res.data;
      }
      if (form.password && form.password.trim()) {
        setCreatedCredentials({
          username: payload.username,
          password: form.password,
          station_id: payload.station_id,
        });
      }

      setDialogOpen(false);
      setEditing(null);
      setMessage({ type: 'success', text: 'WebRTC contributor saved' });
      await loadContributors();
      if (saved?.id) setSelectedContributorId(String(saved.id));
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to save contributor' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this WebRTC contributor account?')) return;
    try {
      await axios.delete(`${API_URL}/api/webrtc-contributors/${id}`, { withCredentials: true });
      setMessage({ type: 'success', text: 'WebRTC contributor deleted' });
      if (String(selectedContributorId) === String(id)) setSelectedContributorId('');
      await loadContributors();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.message || 'Failed to delete contributor' });
    }
  };

  const stationIdForConfig = useMemo(() => {
    if (selectedContributor?.station_id) return selectedContributor.station_id;
    const chosenStationId = parseInt(setupStationId, 10);
    if (Number.isInteger(chosenStationId)) return chosenStationId;
    return connectionInfo?.sampleConfig?.stationId || 1;
  }, [selectedContributor, setupStationId, connectionInfo]);

  const sampleConfigText = useMemo(() => {
    const base = connectionInfo?.sampleConfig || {
      serverHost: API_URL,
      signalingPort: 8004,
      stationId: stationIdForConfig,
      username: selectedContributor?.username || 'contributor_user',
      password: '<password-you-set-for-this-account>',
      reconnectSeconds: 5,
      audio: {
        ffmpegPath: 'ffmpeg',
        inputType: 'alsa',
        device: 'hw:0,0',
        sampleRate: 48000,
        channels: 1,
      },
    };
    const cfg = {
      ...base,
      stationId: stationIdForConfig,
      username: selectedContributor?.username || base.username,
    };
    if (embedPassword) cfg.password = embedPassword;
    else if (createdCredentials && selectedContributor?.username === createdCredentials.username) {
      cfg.password = createdCredentials.password;
    }
    return JSON.stringify(cfg, null, 2);
  }, [connectionInfo, createdCredentials, embedPassword, selectedContributor, stationIdForConfig]);

  const installSteps = connectionInfo?.installSteps?.length
    ? connectionInfo.installSteps
    : FALLBACK_INSTALL_STEPS;

  const copyText = async (text, label = 'Copied') => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage({ type: 'success', text: label });
    } catch (_) {
      setMessage({ type: 'error', text: 'Could not copy to clipboard' });
    }
  };

  const readBlobError = async (data) => {
    if (!data) return null;
    if (typeof data === 'object' && !(data instanceof Blob) && data.message) return data.message;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        return parsed.message || null;
      } catch (_) {
        return null;
      }
    }
    return null;
  };

  const downloadPackage = async () => {
    if (!selectedContributor) {
      setMessage({ type: 'error', text: 'Choose a contributor account first. Create it under Users if it is not listed.' });
      return;
    }
    if (!embedPassword || embedPassword.length < 8) {
      setMessage({ type: 'error', text: 'Enter that account’s password so it can be written into the download.' });
      return;
    }
    if (!stationIdForConfig) {
      setMessage({ type: 'error', text: 'Choose the station this sender contributes to.' });
      return;
    }

    setDownloading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await axios.post(
        `${API_URL}/api/webrtc-contributors/${selectedContributor.id}/sender-package`,
        { password: embedPassword, station_id: stationIdForConfig },
        { withCredentials: true, responseType: 'blob' }
      );

      const contentType = String(response.headers['content-type'] || '');
      if (contentType.includes('application/json') || contentType.includes('text/')) {
        const errMsg = await readBlobError(response.data);
        throw new Error(errMsg || 'Server returned an error instead of the package zip');
      }

      // Guard against JSON error bodies mistakenly returned as 200
      if (response.data instanceof Blob && response.data.size < 512) {
        const head = await response.data.slice(0, 64).text();
        if (head.trim().startsWith('{')) {
          const errMsg = await readBlobError(response.data);
          throw new Error(errMsg || 'Server returned an error instead of the package zip');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `remote-contribution-${selectedContributor.username}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: 'Sender package download started' });
    } catch (e) {
      const blobMsg = await readBlobError(e.response?.data);
      setMessage({
        type: 'error',
        text: blobMsg || e.message || 'Failed to download sender package',
      });
    } finally {
      setDownloading(false);
    }
  };

  const serverHost = connectionInfo?.serverHost || API_URL;
  const signalingHint = connectionInfo?.signalingUrlHint
    || (String(serverHost).startsWith('https')
      ? `${serverHost}/contrib/socket.io (wss — nginx TLS proxy)`
      : `${serverHost.replace(/:\\d+$/, '')}:8004`);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ color: '#22d3ee' }}>
              WebRTC Contribution Accounts
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={downloadPackage}
                disabled={downloading || !selectedContributor || embedPassword.length < 8}
              >
                {downloading ? 'Preparing…' : 'Download Linux Sender'}
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()} sx={{ bgcolor: '#22d3ee' }}>
                Add Account
              </Button>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Third-party sources (satellite news, external studios, etc.) authenticate with these accounts and
            send audio into clock <strong>WebRTC Receiver</strong> slots. Cloud (HTTPS) senders use the TLS
            proxy path automatically; LAN senders use port {connectionInfo?.signalingPort || 8004}.
          </Alert>

          {message.text && (
            <Alert
              severity={message.type === 'error' ? 'error' : 'success'}
              sx={{ mb: 2 }}
              onClose={() => setMessage({ type: '', text: '' })}
            >
              {message.text}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ bgcolor: 'transparent', border: '1px solid var(--rs-border)' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#22d3ee' }}>Username</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Display</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Station</TableCell>
                  <TableCell sx={{ color: '#22d3ee' }}>Enabled</TableCell>
                  <TableCell sx={{ color: '#22d3ee', width: 120 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contributors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
                      No contribution accounts configured.
                    </TableCell>
                  </TableRow>
                ) : (
                  contributors.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      selected={String(selectedContributorId) === String(item.id)}
                      onClick={() => {
                        setSelectedContributorId(String(item.id));
                        setEmbedPassword('');
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{item.username}</TableCell>
                      <TableCell>{item.display_name || '-'}</TableCell>
                      <TableCell>
                        {item.station_id
                          ? stations.find((s) => s.id === item.station_id)?.name || `Station ${item.station_id}`
                          : 'All stations'}
                      </TableCell>
                      <TableCell>{item.is_enabled ? 'Yes' : 'No'}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small" sx={{ color: '#22d3ee' }} onClick={() => openDialog(item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#22d3ee', mb: 1 }}>
            Linux sender — download, credentials &amp; install
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Admins and superadmins download a sender here. Choose a contributor account from User Management,
            enter its password, and the zip is filled in before you download it.
          </Typography>

          {connectionError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {connectionError} Install steps below still apply — use your public RadioSuite URL as serverHost.
            </Alert>
          )}

          {createdCredentials && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCreatedCredentials(null)}>
              Save these credentials now — the password will not be shown again.
              <Box component="pre" sx={{ m: 0, mt: 1, whiteSpace: 'pre-wrap' }}>
                {`Username: ${createdCredentials.username}\nPassword: ${createdCredentials.password}\nStation ID: ${createdCredentials.station_id || 'set in config for the target station'}`}
              </Box>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                sx={{ mt: 1 }}
                onClick={() =>
                  copyText(
                    `username=${createdCredentials.username}\npassword=${createdCredentials.password}\nstationId=${createdCredentials.station_id || stationIdForConfig}`,
                    'Credentials copied'
                  )
                }
              >
                Copy credentials
              </Button>
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'transparent', borderColor: 'var(--rs-border)' }}>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                  Cloud connection
                </Typography>
                <Typography variant="body2"><strong>Server host:</strong> {serverHost}</Typography>
                <Typography variant="body2"><strong>Signaling:</strong> {signalingHint}</Typography>
                <Typography variant="body2">
                  <strong>TLS proxy:</strong>{' '}
                  {connectionInfo
                    ? (connectionInfo.useTlsProxy ? 'Yes (HTTPS cloud)' : 'No (plain HTTP / LAN)')
                    : (String(serverHost).startsWith('https') ? 'Yes (HTTPS cloud)' : 'Unknown')}
                </Typography>
                <Typography variant="body2">
                  <strong>Username:</strong> {selectedContributor?.username || '— create/select an account —'}
                </Typography>
                <Typography variant="body2">
                  <strong>Station ID for config:</strong> {stationIdForConfig}
                  {!selectedContributor?.station_id && (
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}(account allows all stations — set this to the station you contribute to)
                    </Typography>
                  )}
                </Typography>
                {!selectedContributor?.station_id && stations.length > 0 && (
                  <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                    <InputLabel>Target station for sender</InputLabel>
                    <Select
                      label="Target station for sender"
                      value={setupStationId}
                      onChange={(e) => setSetupStationId(e.target.value)}
                    >
                      {stations.map((station) => (
                        <MenuItem key={station.id} value={String(station.id)}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel>Contributor account</InputLabel>
                  <Select
                    label="Contributor account"
                    value={selectedContributorId}
                    onChange={(e) => {
                      setSelectedContributorId(e.target.value);
                      setEmbedPassword('');
                    }}
                  >
                    {contributors.filter((item) => item.is_enabled !== false).length === 0 && (
                      <MenuItem value="" disabled>No contributor accounts yet</MenuItem>
                    )}
                    {contributors.filter((item) => item.is_enabled !== false).map((item) => (
                      <MenuItem key={item.id} value={String(item.id)}>
                        {item.username}{item.display_name ? ` — ${item.display_name}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label="Account password"
                  value={embedPassword}
                  onChange={(e) => setEmbedPassword(e.target.value)}
                  sx={{ mt: 1.5 }}
                  helperText="Written into the download. The server only keeps a hash, so it has to be entered here."
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedContributor
                    ? `This build signs in as ${selectedContributor.username}.`
                    : 'Create the contributor under Users, then choose it here.'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Download />}
                  onClick={downloadPackage}
                  disabled={downloading || !selectedContributor || embedPassword.length < 8}
                  sx={{ mt: 2, bgcolor: '#22d3ee' }}
                >
                  {downloading ? 'Preparing…' : 'Download populated sender'}
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'transparent', borderColor: 'var(--rs-border)', height: '100%' }}>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee', mb: 1 }}>
                  Install on Linux
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                  {installSteps.map((step) => (
                    <Typography component="li" variant="body2" key={step} sx={{ mb: 0.75 }}>
                      {step}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Useful commands: <code>sudo systemctl status remotecontribution-headless.service</code>
                  {' · '}
                  <code>journalctl -u remotecontribution-headless.service -f</code>
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#22d3ee' }}>
                  Sample config/default.json
                </Typography>
                <Button size="small" startIcon={<ContentCopy />} onClick={() => copyText(sampleConfigText, 'Config copied')}>
                  Copy config
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={12}
                value={sampleConfigText}
                InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit WebRTC Contributor' : 'New WebRTC Contributor'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={editing ? 'Password (leave blank to keep)' : 'Password'}
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Display Name"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Station</InputLabel>
                <Select
                  label="Station"
                  value={form.station_id || ''}
                  onChange={(e) => setForm({ ...form, station_id: e.target.value || '' })}
                >
                  <MenuItem value="">All stations</MenuItem>
                  {stations.map((station) => (
                    <MenuItem key={station.id} value={station.id}>{station.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} />}
                label="Enabled"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                minRows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
